import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FormRenderer, isVisible, LAYOUT_TYPES } from "@/components/forms/FormRenderer";
import { StaffReview } from "@/components/forms/StaffReview";
import { REVIEW_STATUS, STATUS_CLS } from "@/pages/reviews/MyReviews";
import { ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";

const STAFF_PAGE = "__staff__", REVIEW_PAGE = "__review__";

function buildPages(fields) {
  const pages = [];
  fields.forEach((f) => {
    if (f.field_type === "section" || !pages.length) pages.push({ title: f.field_type === "section" ? f.label : "Introduction", fields: [] });
    pages[pages.length - 1].fields.push(f);
  });
  const marker = fields.find((f) => f.field_type === "staff_review");
  if (marker) pages.forEach((p) => { p.staff = p.fields.includes(marker); });
  else {
    const sp = pages.find((p) => /staff/i.test(p.title));
    if (sp) sp.staff = true; else pages.push({ title: "Agency Staff", key: STAFF_PAGE, fields: [], staff: true });
  }
  pages.push({ title: "Review Answers & Submit", key: REVIEW_PAGE, fields: [] });
  return pages;
}

const staffPositions = (fields) => fields.find((f) => f.field_type === "staff_review")?.choices?.length ? fields.find((f) => f.field_type === "staff_review").choices : undefined;

function completion(fields, answers) {
  const inputs = fields.filter((f) => !LAYOUT_TYPES.includes(f.field_type) && isVisible(f, answers));
  const done = inputs.filter((f) => { const v = answers[f.id]; return !(v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)); });
  return { done: done.length, total: inputs.length };
}

function AdminView({ d, reload }) {
  const latest = d.responses[d.responses.length - 1];
  const reopen = async () => {
    const reason = window.prompt("Reason for reopening (shown in history):") ?? null;
    if (reason === null) return;
    try { await api.post(`/form-assignments/${d.assignment.id}/reopen`, { reason }); toast.success("Survey reopened"); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="review-admin-view">
      {d.can_reopen && d.assignment.status === "submitted" && <Button variant="outline" onClick={reopen} data-testid="review-reopen-btn">Reopen for corrections</Button>}
      {!latest ? <p className="text-sm text-slate-500">Not submitted yet.</p> : (<>
        <p className="text-xs text-slate-500" data-testid="review-revision">Revision {latest.revision} · submitted {new Date(latest.submitted_at).toLocaleString()} by {latest.submitted_by_name}
          {latest.changed_codes?.length ? ` · changed since previous: ${latest.changed_codes.join(", ")}` : ""}</p>
        <FormRenderer fields={d.fields} values={latest.answers} disabled />
        <div className="rounded-xl border border-slate-200 p-4"><h3 className="font-semibold text-slate-900 mb-2">Agency Staff</h3>
          <StaffReview staff={latest.staff_snapshot?.people || d.staff} value={latest.staff_review || {}} onChange={() => {}} disabled positions={staffPositions(d.fields)} />
          {latest.staff_snapshot?.as_of && <p className="text-[11px] text-slate-400 mt-2">Staff snapshot as of {new Date(latest.staff_snapshot.as_of).toLocaleDateString()}</p>}</div>
        {Object.keys(latest.contact_updates || {}).length > 0 && <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm" data-testid="review-contact-updates">
          <p className="font-semibold text-amber-900 mb-1">Proposed contact updates (not yet applied)</p>
          {Object.entries(latest.contact_updates).map(([k, v]) => <p key={k} className="text-amber-900">{k}: {String(v)}</p>)}</div>}
      </>)}
      <div className="text-xs text-slate-500"><p className="font-semibold mb-1">History</p>{(d.assignment.history || []).map((h, i) => <p key={i}>{h.action} · {h.by_name} · {new Date(h.at).toLocaleString()}{h.note ? ` · ${h.note}` : ""}</p>)}</div>
    </div>
  );
}

export default function ReviewRunner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [answers, setAnswers] = useState({});
  const [staff, setStaff] = useState({});
  const [page, setPage] = useState(0);
  const [savedAt, setSavedAt] = useState(null);
  const dirty = useRef(false);
  const load = useCallback(() => {
    api.get(`/form-assignments/${id}`).then((r) => {
      setD(r.data); setAnswers(r.data.answers || {}); setStaff(r.data.assignment.staff_review || {}); setPage(r.data.assignment.current_section || 0);
    }).catch((e) => { toast.error(apiError(e)); navigate("/my-reviews"); });
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);
  const pages = useMemo(() => buildPages(d?.fields || []), [d]);
  const prog = useMemo(() => completion(d?.fields || [], answers), [d, answers]);
  const locked = !d || !d.is_owner || d.assignment.status === "submitted";
  const save = useCallback(async (pg = page) => {
    if (locked) return;
    try { const { data } = await api.patch(`/form-assignments/${id}/draft`, { draft_answers: answers, staff_review: staff, current_section: pg }); setSavedAt(data.saved_at); dirty.current = false;
      setD((x) => (x && x.assignment.status !== data.status ? { ...x, assignment: { ...x.assignment, status: data.status } } : x)); }
    catch (e) { toast.error(apiError(e)); }
  }, [id, answers, staff, page, locked]);
  useEffect(() => {
    if (!dirty.current || locked) return undefined;
    const t = setTimeout(() => save(), 1500);
    return () => clearTimeout(t);
  }, [answers, staff, save, locked]);
  if (!d) return <p className="text-sm text-slate-400 p-6">Loading…</p>;
  const go = async (n) => { await save(n); setPage(n); window.scrollTo(0, 0); };
  const submit = async () => {
    await save();
    try { await api.post(`/form-assignments/${id}/submit`); toast.success("Thank you — your review has been submitted"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const cur = pages[page] || pages[0];
  const header = (
    <div className="mb-5">
      <button onClick={() => navigate(d.is_owner ? "/my-reviews" : -1)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-brand mb-2" data-testid="review-back"><ArrowLeft className="w-4 h-4" />Back</button>
      <div className="flex flex-wrap items-center gap-3"><h1 className="font-display text-2xl font-bold text-slate-900">{d.assignment.form_name}</h1>
        <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${STATUS_CLS[d.assignment.status]}`} data-testid="review-status">{REVIEW_STATUS[d.assignment.status]}</span></div>
      <p className="text-sm text-slate-500">{d.person_name} · Review year {d.assignment.review_year} · Planning year {d.assignment.planning_year}</p>
    </div>
  );
  if (!d.is_owner) return <div className="max-w-3xl">{header}<AdminView d={d} reload={load} /></div>;
  if (d.assignment.status === "submitted") return (
    <div className="max-w-3xl">{header}
      <div className="flex items-center gap-2 rounded-lg bg-emerald-50 text-emerald-800 text-sm p-3 mb-4" data-testid="review-locked"><Lock className="w-4 h-4" />
        Thank you — your survey was submitted{d.assignment.history?.length ? ` on ${new Date([...d.assignment.history].reverse().find((h) => /submitted/.test(h.action))?.at || Date.now()).toLocaleDateString()}` : ""} and is now locked. Contact your District Office if something needs correcting.</div>
      <FormRenderer fields={d.fields.filter((f) => f.field_type !== "staff_review")} values={answers} disabled />
      <div className="rounded-xl border border-slate-200 p-4 mt-4"><h3 className="font-semibold text-slate-900 mb-2">Agency Staff & Producers</h3>
        <StaffReview staff={d.staff} value={staff} onChange={() => {}} disabled positions={staffPositions(d.fields)} /></div></div>
  );
  return (
    <div className="max-w-3xl">{header}
      <div className="mb-5" data-testid="review-progress">
        <div className="flex flex-wrap justify-between gap-1 text-xs text-slate-500 mb-1"><span data-testid="review-section-label">Page {page + 1} of {pages.length} · {cur.title}</span>
          <span data-testid="review-completion">{prog.done} of {prog.total} answered{savedAt ? ` · Saved ${new Date(savedAt).toLocaleTimeString()}` : ""}</span></div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-brand transition-[width]" style={{ width: `${((page + 1) / pages.length) * 100}%` }} /></div>
        <div className="flex flex-wrap gap-1 mt-2">{pages.map((p, i) => <button key={i} onClick={() => go(i)} title={p.title} data-testid={`review-jump-${i}`}
          className={`h-1.5 w-6 rounded-full ${i === page ? "bg-brand" : i < page ? "bg-sky-300" : "bg-slate-200"}`} />)}</div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`review-page-${page}`}>
        {cur.key === REVIEW_PAGE ? (
          <div className="space-y-3" data-testid="review-final"><h3 className="font-semibold text-slate-900">Review Your Answers</h3>
            <p className="text-sm text-slate-600">Check your answers below. Select a section to change anything. After you submit, your survey is locked and sent to your District Office.</p>
            <ul className="text-sm text-slate-600 list-disc pl-5">{pages.slice(0, -1).map((p, i) => <li key={i}><button className="text-brand hover:underline" onClick={() => go(i)} data-testid={`review-goto-${i}`}>{p.title}</button></li>)}</ul>
            <div className="border-t pt-3"><FormRenderer fields={d.fields.filter((f) => f.field_type !== "staff_review" && f.field_type !== "display")} values={answers} disabled dense /></div></div>
        ) : (<>
          {cur.key === STAFF_PAGE && <h3 className="font-semibold text-slate-900 mb-3">Agency Staff</h3>}
          {cur.fields.length > 0 && <FormRenderer fields={cur.fields.map((f) => (f.field_type === "staff_review" ? { ...f, field_type: "question" } : f))} values={answers} onChange={(v) => { dirty.current = true; setAnswers(v); }} />}
          {cur.staff && <div className="mt-4"><StaffReview staff={d.staff} value={staff} positions={staffPositions(d.fields)} onChange={(v) => { dirty.current = true; setStaff(v); }} /></div>}
        </>)}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <Button variant="outline" disabled={page === 0} onClick={() => go(page - 1)} data-testid="review-prev">Previous Section</Button>
        <Button variant="ghost" onClick={async () => { await save(); toast.success("Progress saved"); navigate("/my-reviews"); }} data-testid="review-save-exit">Save &amp; Exit</Button>
        <div className="ml-auto">{cur.key === REVIEW_PAGE
          ? <Button className="bg-brand hover:bg-brand-strong" onClick={submit} data-testid="review-submit">Submit Survey</Button>
          : <Button className="bg-brand hover:bg-brand-strong" onClick={() => go(page + 1)} data-testid="review-next">{page === pages.length - 2 ? "Review Answers" : "Next Section"}</Button>}</div>
      </div>
    </div>
  );
}
